import React from "react"
import SubsectionContainer from "../shared/SubsectionContainer"
import { PublicLocation } from "../../../domain/Profile/schema"
import { useCMSReady } from "../../../domain/CMS/useCMSReady"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { FeatureFlag } from "../../../domain/enums/featureFlags"
import { LIST_ITEM_ID_PREFIX } from "./Locations/LocationListItem"
import ColumnContainer from "../../shared/components/Layout/ColumnContainer"
import Typography, { typographyTypes } from "../../shared/components/Typography"
import { ButtonWrapper } from "../../shared/components/ButtonWrapper"

interface LocationProps {
  locations?: PublicLocation[]
  setSelectedMarkerId?: React.Dispatch<React.SetStateAction<string>>
  hasVirtualServices?: boolean
}

export const Location: React.FC<LocationProps> = ({
  locations,
  setSelectedMarkerId,
  hasVirtualServices = false,
}) => {
  const { cms: cmsContent } = useCMSReady(["common", "publicProfileSlide"])
  const commonText = cmsContent.common
  const publicProfileText = cmsContent.publicProfileSlide

  const isNoVirtualLocationEnabled = useFeatureFlagEnabled(
    FeatureFlag.IsNoVirtualLocationEnabled,
  )

  const VIRTUAL = "virtual"
  const ENTER = "Enter"

  const primaryLocation =
    locations?.length === 1
      ? locations[0]
      : locations?.find((location) => location.isPrimary)
  const nonPrimaryLocations = locations?.filter(
    (location) => !location.isPrimary,
  )

  const handleSort = (locations: PublicLocation[]): PublicLocation[] => {
    if (isNoVirtualLocationEnabled) {
      return [...locations].sort((a, b) => {
        const cityComparison = (a.city ?? "").localeCompare(b.city ?? "")
        return cityComparison === 0
          ? (a.line1 ?? "").localeCompare(b.line1 ?? "")
          : cityComparison
      })
    }

    const virtualList = locations.filter(
      (location) => location.locationTypeDisplayId === VIRTUAL,
    )
    const nonVirtualList = locations.filter(
      (location) => location.locationTypeDisplayId !== VIRTUAL,
    )
    const sortedList = nonVirtualList.sort((a, b) => {
      const cityComparison = (a.city ?? "").localeCompare(b.city ?? "")
      if (cityComparison === 0) {
        return (a.line1 ?? "").localeCompare(b.line1 ?? "")
      }
      return cityComparison
    })
    return [...sortedList, ...virtualList]
  }

  const repeatedCityReferenceRecord: Record<string, number> = {}
  for (const location of nonPrimaryLocations || []) {
    const { city } = location
    if (city) {
      repeatedCityReferenceRecord[city] =
        (repeatedCityReferenceRecord[city] || 0) + 1
    }
  }

  const handleLocationClick = (locationId: string) => {
    setSelectedMarkerId?.(locationId)
    const locationElement = document.getElementById(
      `${LIST_ITEM_ID_PREFIX}${locationId}`,
    )
    if (locationElement) {
      const elementHeight = locationElement.offsetHeight
      locationElement.style.scrollMarginBottom = `${elementHeight}px`
      locationElement.scrollIntoView({ behavior: "smooth", block: "end" })
    }
  }

  const getLocationLinkText = (location: PublicLocation) => {
    if (isNoVirtualLocationEnabled) {
      const city = location.city ?? ""
      return `${city}${repeatedCityReferenceRecord[city] > 1 ? ` - ${location.line1}` : ""}`
    }

    const { locationTypeDisplayId } = location
    const city = location.city ?? ""
    const text =
      locationTypeDisplayId === VIRTUAL
        ? commonText?.virtual
        : `${city}${repeatedCityReferenceRecord[city] > 1 ? ` - ${location.line1}` : ""}`
    return text
  }

  const showOtherLocations = isNoVirtualLocationEnabled
    ? (nonPrimaryLocations?.length ?? 0) > 0
    : !!locations?.length && locations?.length > 1 && !!nonPrimaryLocations

  return (
    <SubsectionContainer>
      <ColumnContainer styleOverrides="space-y-0">
        <Typography
          text={publicProfileText?.locationPrimary}
          variant={typographyTypes.NORMAL}
          styleOverrides="font-medium mb-1"
        />
        {isNoVirtualLocationEnabled ? (
          primaryLocation ? (
            <>
              <Typography
                text={primaryLocation.line1}
                variant={typographyTypes.NORMAL}
              />
              {primaryLocation.line2 && (
                <Typography
                  text={primaryLocation.line2}
                  variant={typographyTypes.NORMAL}
                />
              )}
              <Typography
                text={`${primaryLocation.city}${primaryLocation.stateOrTerritory || primaryLocation.postalCode ? "," : ""} ${primaryLocation.stateOrTerritory} ${primaryLocation.postalCode}`}
                variant={typographyTypes.NORMAL}
              />
            </>
          ) : hasVirtualServices ? (
            <Typography
              text={publicProfileText?.locationsOnlineOnlyPractice}
              variant={typographyTypes.NORMAL}
            />
          ) : null
        ) : primaryLocation &&
          primaryLocation?.locationTypeDisplayId !== VIRTUAL ? (
          <>
            <Typography
              text={primaryLocation.line1}
              variant={typographyTypes.NORMAL}
            />
            {primaryLocation.line2 && (
              <Typography
                text={primaryLocation.line2}
                variant={typographyTypes.NORMAL}
              />
            )}
            <Typography
              text={`${primaryLocation.city}${primaryLocation.stateOrTerritory || primaryLocation.postalCode ? "," : ""} ${primaryLocation.stateOrTerritory} ${primaryLocation.postalCode}`}
              variant={typographyTypes.NORMAL}
            />
          </>
        ) : (
          <Typography
            text={publicProfileText?.locationsOnlinePractice}
            variant={typographyTypes.NORMAL}
          />
        )}
      </ColumnContainer>
      {showOtherLocations && (
        <ColumnContainer styleOverrides="space-y-0">
          <Typography
            text={publicProfileText?.locationsOther}
            variant={typographyTypes.NORMAL}
            styleOverrides="font-medium mb-1"
          />
          {handleSort(nonPrimaryLocations ?? []).map((location) => (
            <ButtonWrapper
              key={location.id}
              onClick={() => handleLocationClick(location.id)}
              onKeyDown={(e) => {
                if (e.key === ENTER) {
                  handleLocationClick(location.id)
                }
              }}
              styleOverrides="p-0 pb-1"
              childStyleOverrides="text-left"
            >
              <Typography
                text={getLocationLinkText(location)}
                variant={typographyTypes.NORMAL}
                styleOverrides="underline text-link-blue"
              />
            </ButtonWrapper>
          ))}
        </ColumnContainer>
      )}
    </SubsectionContainer>
  )
}
